/** @jsxImportSource @emotion/react */
import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { MenuItem, useTheme } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '@app/redux/store'
import { usePreferences } from '@app/hooks/usePreferences'
import { exportButton } from './ExportRiskSummaryXlxs.styles'
import { useAtomValue } from 'jotai'
import { clientNameAtom } from '@app/state/clientsAtom'
import { Polygon, Position } from 'geojson'
import { RegionOption } from '../../RegionFilter'
import apiClient from '@app/config/axios.config'
import {
  blobToDataUrl,
  preparePageDuplicateMapAndExportImageBlob,
} from '@app/components/Pages/Map/MapTools/ExportMap/mapExporter'
import { generateVulnerabilityPalette } from '../../MapView/RiskMapView/RiskMapView.utilities'
import * as turf from '@turf/turf'
import { getLayerId } from '../../RiskSideDrawerContent/data_fetchers/layerDetailsFetcher'
import { pollForReport } from '@app/utils/pollForReport'
import { toast } from '@app/components/ui/Toast/toast'
import { userAtom } from '@app/hooks/useUserProfile'
import { logDataDogAction } from '@app/utils/logger'
import { DATADOG_ACTIONS } from '@app/app-constants'

export const RiskSummaryReportButton = () => {
  const theme = useTheme()
  const { layers, drawAreas, legendsData } = useSelector((state: RootState) => state.riskMap)
  const projectedRiskChartState = useSelector((state: RootState) => state.projectedRiskChartState)
  const { term_preference } = usePreferences()
  const { regionMasks, map, cascadingAssetNDays } = useMap()
  const vulnerabilityPalette = generateVulnerabilityPalette(legendsData, 'vulnerability')
  const clientName = useAtomValue(clientNameAtom)
  const { data: user } = useAtomValue(userAtom)

  const handleClick = async () => {
    if (!map) return
    toast({
      description:
        'Generating data for report. This may take a while, we will notify you when the report is ready.',
      type: 'info',
    })

    const regions = regionMasks ? regionMasks.map((mask: RegionOption) => mask.region) : []
    const draw_area_coordinates: Position[][] | null =
      drawAreas.length > 0 ? (drawAreas[0].geometry as Polygon).coordinates : null

    let mapImageDataUrl = ''
    const mapWidth = 595
    const mapHeight = 559
    await preparePageDuplicateMapAndExportImageBlob(
      map,
      mapWidth,
      mapHeight,
      async (imageBlob: Blob) => {
        mapImageDataUrl = await blobToDataUrl(imageBlob)
      },
      vulnerabilityPalette,
      null,
      theme,
    )

    const mapHeightInPixels = map.getCanvas().height
    const mapWidthInPixels = map.getCanvas().width
    const mapWidthInMetres = turf.distance(
      map.unproject([0, mapHeightInPixels / 2.0]).toArray(),
      map.unproject([mapWidthInPixels, mapHeightInPixels / 2.0]).toArray(),
      { units: 'meters' },
    )

    try {
      const mapImageUploadResponse = await apiClient.post(`/report/mapimage`, {
        dataUrl: mapImageDataUrl,
      })

      logDataDogAction(DATADOG_ACTIONS.EXPORT_REPORT, {
        type: 'risk-summary',
        format: 'pdf',
      })

      const mapImageDownloadUrl = mapImageUploadResponse.data as string
      const layer_ids = layers.map((l) => getLayerId(l))

      const report_id = `${user?.user_uuid}-${Date.now()}`

      await apiClient.post(`/report/export/${clientName}/risksummary-pdf/${report_id}`, {
        layer_ids,
        draw_area_coordinates,
        regions,
        mapImage: {
          bearing: map.getBearing(),
          pitch: map.getPitch(),
          mapWidthInMetres,
          url: mapImageDownloadUrl,
        },
        projectedRiskChartState,
        cascadingAssetNDays,
      })

      const downloadFilename = term_preference.hazard
        ? 'Resilience Explorer® Hazard Summary'
        : `Resilience Explorer® Risk Summary`

      pollForReport(report_id, downloadFilename)
    } catch (error) {
      toast({
        description:
          'There was an error generating the report. Please notify your Urban Intelligence contact or submit feedback.',
        type: 'error',
      })
    }
  }

  return (
    <MenuItem css={exportButton({ theme })} onClick={handleClick}>
      Report (PDF)
    </MenuItem>
  )
}
